/*
  State model for synth subtest results, accessible from the test model via 'agentResults' property
  Subtest results are stored in an observable map of AgentTestResultsState objects, keyed by agent id.
  These individual models are further broken down to contain an observable map of test results per target (AgentTargetTestResultsState).
*/
import { action, computed, observable } from 'mobx';
import { isIpValid } from 'core/util/ip';
import moment from 'moment';
import { URL_DEFINED_TEST_TYPES } from '@kentik/ui-shared/synthetics/constants';
import { DEFAULT_MESH_LOOKBACK, SYNGEST_CUTOVER } from 'app/util/constants';
import { parseQueryString } from 'app/util/utils';
import { getTimelinebounds, setLookbackFromHistory } from 'app/views/synthetics/utils/syntheticsUtils';
import { getStartAndEndFromLookback } from 'core/util/dateUtils';
import { processTestResults, processTraceResults, enrichTracerouteLookups, aggregateSubtestResults } from './utils';
import AgentTestResultsState from './AgentTestResultsState';
import TestResultsState from './TestResultsState';

// the number of intervals we want to show as a trend of test results
// ex: a 1 hour lookback would be trended to the last 4 hours
const TREND_INTERVAL_SIZE = 4;

class AgentResultsState extends TestResultsState {
  get defaults() {
    return {
      ...super.defaults,
      agents: observable.map({}, { deep: false }),
      agentResults: null,
      loadingTraceResults: true,
      hasAgentResults: false,
      traceResultTimeMs: null,
      pageTabId: 'results',
      // trend results
      loadingTrendResults: false,
      loadingTrendDetailResults: false,
      hasTrendResults: false,
      trendResults: null,
      trendAggregationData: {},
      trendAgents: observable.map({}, { deep: false }),
      trendIntervals: new Array(TREND_INTERVAL_SIZE).fill(null).map(() => ({
        from: null,
        to: null,
        results: null
      })),
      selectedTrendDetailIndex: TREND_INTERVAL_SIZE - 1
    };
  }

  // an observable map of AgentTestResultsState objects, keyed by agent id
  @observable.ref
  agents = this.defaults.agents;

  // subtest results
  @observable.ref
  agentResults = this.defaults.agentResults;

  @observable
  hasAgentResults = this.defaults.hasAgentResults;

  // loading indicator representing the fetch for the entire trended query
  @observable
  loadingTrendResults = this.defaults.loadingTrendResults;

  // loading indicator used when fetching an individual interval in a trend query
  // after fetching, the interval results are cached
  @observable
  loadingTrendDetailResults = this.defaults.loadingTrendDetailResults;

  // indicates a valid set of trend results
  @observable
  hasTrendResults = this.defaults.hasTrendResults;

  // the test results for the entire trend query
  @observable.ref
  trendResults = this.defaults.trendResults;

  // currently used to report on whether test results are aggregated
  @observable.ref
  trendAggregationData = this.defaults.trendAggregationData;

  // an observable map of AgentTestResultsState objects, keyed by agent id
  @observable.ref
  trendAgents = this.defaults.trendAgents;

  // an array sized by the number of supported intervals we want to report on in a trend
  // each entry will have a from/to date range and a place to cache results as they're loaded
  @observable.ref
  trendIntervals = this.defaults.trendIntervals;

  // the index of the currently selected trend interval
  @observable
  selectedTrendDetailIndex = this.defaults.selectedTrendDetailIndex;

  // map indicating previously loaded hashes
  loadedResultsHashes = {};

  // map indicating previously loaded hashes
  loadedTraceResultsHashes = {};

  configure({ startDate, endDate, lookbackSeconds, history, agent_id, target, force = false, ...displayOptions }) {
    const { test } = this;
    const { hasPing, hasBGPMonitoring, isPreview } = test;
    const isDns = test.get('test_type') === 'dns';

    // capture the export timestamp if passed in
    this.updateExportTime(history);

    if (agent_id) {
      this.setupAgentResults(agent_id, target);
    }

    if (!this.init || !this.hasLoadedResults(agent_id, target)) {
      Object.entries(displayOptions).forEach(([key, value]) => {
        if (this.displayOptions[key] !== undefined) {
          this.displayOptions[key] = value;
        }
      });

      const { usePageComponent, shared } = this.displayOptions;

      this.displayOptions.allowMesh = !isDns;
      this.displayOptions.allowSankey = !isDns;
      this.displayOptions.allowMap = !isDns || !this.store.$app.isExport;
      this.displayOptions.allowPath = hasPing;
      this.displayOptions.allowHealthTimeline = !isPreview;
      this.displayOptions.allowBgp = hasBGPMonitoring && usePageComponent && !isPreview;
      this.displayOptions.allowAgentDetails = !(shared || isPreview);
      this.history = history;
      this.agent_id = agent_id;
      this.target = target;

      this.initiate({ startDate, endDate, lookbackSeconds });
    } else if (
      force ||
      (startDate && this.startDate !== startDate) ||
      (endDate && this.endDate !== endDate) ||
      (lookbackSeconds && this.lookbackSeconds !== lookbackSeconds)
    ) {
      this.onDateRangeChange({ startDate, endDate, lookbackSeconds });
    }
  }

  initiate({ startDate, endDate, lookbackSeconds }) {
    const { history, test } = this;
    const { $exports } = this.store;
    const { allowSettings } = this.displayOptions;
    const hasLocationHistory =
      this.checkForDateRange(history?.location?.state || {}) || parseQueryString(history?.location?.search).start;
    const hasBeenProvededADateRange = this.checkForDateRange({ startDate, endDate, lookbackSeconds });
    const defaultLookbackSeconds = this.test.isMesh ? DEFAULT_MESH_LOOKBACK : this.lookbackSeconds;
    let initPromise = Promise.resolve({
      startDate: this.startDate,
      endDate: this.endDate,
      lookbackSeconds: defaultLookbackSeconds
    });

    // we support to many ways to configure date range
    if (hasBeenProvededADateRange) {
      initPromise = Promise.resolve({ startDate, endDate, lookbackSeconds });
    } else if (hasLocationHistory && allowSettings) {
      initPromise = setLookbackFromHistory({ $exports, history, test }).then(() => null);
    }

    return initPromise
      .then((dateObj) => {
        if (allowSettings) {
          return $exports
            .getSettings()
            .then(({ hashedPageTabId, hashedLookbackSeconds, hashedStartDate, hashedEndDate }) => {
              if (hashedPageTabId !== undefined) {
                this.onPageTabChange(hashedPageTabId);
              }

              const hasDateRangeSettings = this.checkForDateRange({
                startDate: hashedStartDate,
                endDate: hashedEndDate,
                lookbackSeconds: hashedLookbackSeconds
              });

              if (hasDateRangeSettings && !hasBeenProvededADateRange) {
                return {
                  startDate: hashedStartDate,
                  endDate: hashedEndDate,
                  lookbackSeconds: hashedLookbackSeconds
                };
              }
              return dateObj;
            });
        }
        return dateObj;
      })
      .then((dateObj) => {
        this.onDateRangeChange(dateObj);
        this.init = true;
      });
  }

  // ----- fetching -------------------------------------

  // fetches fresh alarms, test results, trend test results, and trace results when the selected date changes
  fetch({ startDate, endDate, trendStartDate }) {
    this.newRequestID();
    this.buildTrendIntervals({ startDate: trendStartDate, endDate });
    this.fetchTestResults({ start_time: startDate, end_time: endDate });
    this.fetchTrendTestResults({ start_time: trendStartDate, end_time: endDate });
    this.fetchTraceResults({ start_time: startDate, end_time: endDate });
    this.fetchAlarmResults({
      start: moment.utc(startDate * 1000).toISOString(),
      end: moment.utc(endDate * 1000).toISOString()
    });
  }

  // based on the defined interval size, evenly divides into a list of date range arguments
  // we can use to jump between subtest results segments on the trend charts
  // these trend intervals also house cached results as they're loaded
  @action
  buildTrendIntervals({ startDate, endDate }) {
    // equally divide the time range by the number of trend intervals we want
    const intervalSize = Math.floor((endDate - startDate) / TREND_INTERVAL_SIZE);

    let fromTs = startDate;

    for (let i = 0; i < TREND_INTERVAL_SIZE; i++) {
      const from = fromTs;
      const fromMs = fromTs * 1000;
      const to = fromTs + intervalSize;
      const toMs = to * 1000;

      // populate the date range for the given interval
      this.trendIntervals[i] = {
        // from/to we'll use for test results queries
        from,
        to,
        // fromMs/toMs we'll use for chart plotting
        fromMs,
        toMs
      };

      // start the next interval where we left off
      fromTs += intervalSize;
    }
  }

  // post-processor for subtest results
  // sets data properties, manages loading indicators, and calculates min/max metrics for the ui
  processFetchTestResultsResponse(test, results) {
    const hasResults = this.checkForTestResults(results);

    if (hasResults) {
      const { health_ts = {} } = results;
      const tsKeys = Object.keys(health_ts);
      let timeMs = tsKeys[tsKeys.length - 1];
      const processedTestResults = processTestResults(test, results);
      const agentIds = Object.keys(processedTestResults);

      if (this.exportTime) {
        // override the selected timestamp when exporting
        // note that we want an explicit null value when we're exporting any results other than waterfall
        // this is so we don't litter the export with hovered tooltips and markers
        timeMs = this.selectedTabId === 'waterfall' ? this.exportTime : null;
      }

      this.agentResults = results;
      this.timestamps = Object.keys(results?.health_ts || {}).sort();
      this.resultTime = +timeMs;

      // strip out the avg aggregate results for processing
      const { results: aggregateResults, ...aggregationData } = results?.book?.aggregationData || {};

      // merge the subtest aggregates into a form that we can hydrate down the chain
      const aggregates = aggregateSubtestResults({
        aggregateResults,
        testResults: processedTestResults
      });

      // capture the max raw lookback and aggregation state of the syngest results
      this.aggregationData = aggregationData;

      for (let i = 0; i < agentIds.length; i += 1) {
        const agentId = agentIds[i];
        this.setupAgentResults(agentId);
        this.agents.get(agentId).setTestResults(results, processedTestResults[agentId], aggregates?.[agentId]);
      }
    }

    this.hasAgentResults = hasResults;
    this.loadingResults = false;
  }

  // see above - this does not do any aggregate calculations
  processFetchTrendTestResultsResponse(test, results) {
    const hasResults = this.checkForTestResults(results);
    if (hasResults) {
      const processedTestResults = processTestResults(test, results);
      const agentIds = Object.keys(processedTestResults);

      this.trendResults = results;

      // capture the max raw lookback and aggregation state of the syngest results
      this.trendAggregationData = results?.book?.aggregationData || {};

      for (let i = 0; i < agentIds.length; i += 1) {
        const agentId = agentIds[i];
        // sets up an AgentTestResultsState for the given agent if need be
        this.setupTrendAgentResults(agentId);

        // the AgentTestResultsState then sets the results as an AgentTargetTestResultsState
        // this final AgentTargetTestResultsState contains getters we use for things like httpHealthTs, pingHealthTs etc
        this.trendAgents.get(agentId).setTestResults(results, processedTestResults[agentId]);
      }
    }

    this.hasTrendResults = hasResults;
    this.loadingTrendResults = this.defaults.loadingTrendResults;
  }

  // fetch subtest results for the given date range
  @action
  fetchTestResults(dateObj) {
    const { $syn } = this.store;
    const { requestId, test } = this;
    const { isPreset } = this.test;

    // reset state
    this.agentResults = this.defaults.agentResults;
    this.timestamps = this.defaults.timestamps;
    this.resultTime = this.defaults.resultTime;
    this.aggregationData = this.defaults.aggregationData;
    this.hasAgentResults = this.defaults.hasAgentResults;
    this.loadingResults = true;

    // use 'withAggregation' to explicitly ask for avg aggregate results
    const req = { ...dateObj, ids: [test.id], withAggregation: true };
    let hash = test.id;
    if (this.agent_id) {
      req.agent_ids = [this.agent_id];
      hash += `.${this.agent_id}`;
    }

    /*
      Send targets in the syngest request for all test types except url, page load, and transaction.
      If we sent targets for url and page load, we would not have ping/knock task data returned.
      This is because the target for ping/knock task types chops down the original target to be just the hostname and would not match.
      Transaction tests do not send their targets because they would never match a task.

      It's particularly important to send in the target when fetching subtest results for a2a/mesh tests because:
        1. Instead of pulling all subtests for the given source agent, we will only pull the subtest for the source/target pair
        2. The aggregate average returned from syngest will be accurate
    */
    if (this.target && !URL_DEFINED_TEST_TYPES.includes(test.get('test_type'))) {
      req.targets = [this.target];
      hash += `.${this.target}`;
    }

    return $syn.requests.fetchTestResults(req, { preset: isPreset }).then(
      action((resp) => {
        const results = resp.tests_health?.[test.id] || {};

        // check if request is relevant before storing response
        if (requestId === this.requestId) {
          this.processFetchTestResultsResponse(test, results);
          this.loadedResultsHashes[hash] = true;

          // cache the test result for trends to use
          this.trendIntervals[this.selectedTrendDetailIndex].results = results;
        }
      })
    );
  }

  // fetch subtest results for the given date range
  @action
  fetchTrendTestResults(dateObj) {
    const { $syn } = this.store;
    const { requestId, test } = this;
    const { isPreset } = this.test;

    // reset state
    this.trendResults = this.defaults.trendResults;
    this.trendAggregationData = this.defaults.trendAggregationData;
    this.hasTrendResults = this.defaults.hasTrendResults;
    this.selectedTrendDetailIndex = this.defaults.selectedTrendDetailIndex;
    this.loadingTrendResults = true;

    const req = { ...dateObj, ids: [test.id] };
    if (this.agent_id) {
      req.agent_ids = [this.agent_id];
    }

    // see fetchTestResults
    if (this.target && !URL_DEFINED_TEST_TYPES.includes(test.get('test_type'))) {
      req.targets = [this.target];
    }

    $syn.requests.fetchTestResults(req, { preset: isPreset }).then(
      action((resp) => {
        const results = resp.tests_health?.[test.id] || {};

        // check if request is relevant before storing response
        if (requestId === this.requestId) {
          this.processFetchTrendTestResultsResponse(test, results);
        }
      })
    );
  }

  // loads detail subtest results when the trend selection changes
  // first checks the cache in the 'results' property in the trend intervals before fetching fresh
  @action
  fetchTrendDetailResults({ id }) {
    // protect against string poisioning
    const intervalId = +id;
    // mark the selected detail interval
    this.selectedTrendDetailIndex = intervalId;
    // get the details of the selected interval
    const { from, to, results } = this.trendIntervals[intervalId] || {};

    if (results) {
      // run the cache results through the processor chain
      this.processFetchTestResultsResponse(this.test, results);
    } else if (from && to) {
      // mark the trend detail sections as loading
      this.loadingTrendDetailResults = true;

      this.fetchTestResults({ start_time: from, end_time: to }).finally(
        // reset detail loading state
        action(() => (this.loadingTrendDetailResults = this.defaults.loadingTrendDetailResults))
      );
    }
  }

  // fetches trace results for the given test and selected date range
  // this hydrates the path panel
  @action
  fetchTraceResults(dateObj) {
    const { $syn } = this.store;
    const { requestId, test, agent_id, target } = this;
    const { id, isPreset } = test;
    const { allowPath } = this.displayOptions;

    if (allowPath && !this.hasLoadedResults(agent_id, target, 'trace')) {
      // reset state
      this.traceResults = this.defaults.traceResults;
      this.traceResultTimeMs = this.defaults.traceResultTimeMs;
      this.hasTraceResults = this.defaults.hasTraceResults;
      this.loadingTraceResults = true;
      this.lastFetchedTraceParams = this.defaults.lastFetchedTraceParams;

      const req = { ...dateObj, id };
      let hash = id;
      if (agent_id) {
        req.agent_ids = [+agent_id];
        hash += `.${agent_id}`;
      }

      if (target && isIpValid(target)) {
        req.target_ips = [target];
        hash += `.${target}`;
      }

      let targetAgentId;
      // For mesh and agent tests, we want to set the targetAgentId to be used when processing the trace reuslts
      if (target && (test.isMesh || test.isAgentTest)) {
        targetAgentId = isIpValid(target) ? $syn.agents.getTargetAgentsByIps([target])?.[0]?.id : target;
        req.target_agent_ids = [targetAgentId];
      }

      // for mesh and agent tests, target is now an agentId, so it should still be memoized
      if (target && (test.isMesh || test.isAgentTest)) {
        hash += `.${target}`;
      }

      // For hostname tests, or url based tests, that have trace results, we need the target to be the hostname, because ips change
      let targetHostname;
      if (test.isHostname || test.isUrlDefined) {
        targetHostname = test.agentResults.target;
      }

      // capture the params used to fetch trace results
      // agentIds and targetIps are used to fetch the results
      // allTargets and selectedTargets are returned from the fetch and used to set the selected state
      this.lastFetchedTraceParams = {
        agentIds: req.agent_ids,
        targetIps: req.target_ips,
        allTargets: [],
        selectedTargets: []
      };

      $syn.requests
        .fetchTestTraceroutee(id, req, { preset: isPreset })
        .then(
          action((traceResults) => {
            // check if request is relevant before storing response
            if (requestId === this.requestId) {
              const hasTraceResults = this.checkForTraceResults(traceResults);

              if (hasTraceResults) {
                const { trace_ts = {} } = traceResults?.results || {};
                const tsKeys = Object.keys(trace_ts);
                const timeMs = +tsKeys[tsKeys.length - 1] * 1000;
                const processedTraceResults = processTraceResults(traceResults, targetAgentId, targetHostname, $syn);
                const agentIds = Object.keys(processedTraceResults);

                traceResults.lookups = enrichTracerouteLookups(traceResults.lookups, $syn);

                this.traceResults = traceResults;
                this.traceResultTimeMs = timeMs;
                this.processedTraceResults = processedTraceResults;

                for (let i = 0; i < agentIds.length; i += 1) {
                  const agentId = agentIds[i];
                  this.setupAgentResults(agentId);
                  this.agents.get(agentId).setTraceResults(traceResults, processedTraceResults[agentId]);
                }

                // capture the selected target ips from the last fetch
                // set both to the selected target ips so we have a '1 of 1' selected state in this subtest
                // this is what we'd always expect in a subtest result
                this.lastFetchedTraceParams.allTargets = traceResults?.selectedTargets || [];
                this.lastFetchedTraceParams.selectedTargets = traceResults?.selectedTargets || [];
              }

              this.hasTraceResults = hasTraceResults;
              this.loadingTraceResults = false;
              this.loadedTraceResultsHashes[hash] = true;
            }
          })
        )
        .catch(
          action(() => {
            // reset state on error so we at least remove loading state and can display empty results
            this.lastFetchedTraceParams = this.defaults.lastFetchedTraceParams;
            this.hasTraceResults = this.defaults.hasTraceResults;
            this.loadingTraceResults = false;
            this.traceResults = {};
          })
        );
    }
  }

  // returns a filter for querying against the alert manager alarms api
  // this will always contain the source agent id but a2a/mesh tests will also include the target agent id
  getAlarmFetchFilter() {
    const { test } = this;
    // initialize a filter with the source agent
    const filter = {
      keys: {
        filter: {
          agentId: {
            equals: this.agent_id
          }
        }
      }
    };

    if (test.isMesh || test.isAgentTest) {
      // strengthen the filter to include target agent id for agent/mesh tests
      filter.keys.filter.targetAgentId = {
        equals: this.target
      };
    }

    return filter;
  }

  // fetches alarm results for the given source/target and date range
  @action
  fetchAlarmResults(dateObj) {
    const { $syn } = this.store;
    const { requestId, test } = this;

    // reset state
    this.alarmResults = this.defaults.alarmResults;
    this.hasAlarmResults = this.defaults.hasAlarmResults;
    this.loadingAlarmResults = true;

    return $syn.requests
      .getTestAlarms({ ...dateObj, ids: [test.id], filter: this.getAlarmFetchFilter() }, { preset: test.isPreset })
      .then(
        action((resp) => {
          // check if request is relevant before storing response
          if (requestId === this.requestId) {
            // alarm results will now contain all alarms for the source agent id and in the case of a2a/mesh, a target agent id
            // transaction and agent-targeted tests do not need to do anything further with their alarm results
            // all other test types should filter the selected target to ensure the alarm timeline is populated with accurate
            if (!test.isTransaction && !test.isAgentTest && !test.isMesh && resp?.alarms) {
              resp.alarms = resp.alarms.filter((alarm) => {
                const { target, resolver } = alarm?.details || {};

                if (test.isDns) {
                  return resolver === this.target;
                }

                return target === this.target;
              });
            }

            this.alarmResults = resp;
            this.hasAlarmResults = !!resp?.alarms.length;
            this.loadingAlarmResults = false;
          }
        })
      );
  }

  // initializes an AgentTestResultsState for the given agent id and AgentTargetTestResultsState for the given target if necessary
  @action
  setupAgentResults(id, target) {
    const { $syn } = this.store;

    if (!this.agents.get(id)) {
      this.agents.set(
        id,
        new AgentTestResultsState({
          store: this.store,
          test: this.test,
          agent: $syn.agents.get(id)
        })
      );
    }

    if (target) {
      this.agents.get(id).setupTargetResults(target);
    }
  }

  // see above, but for trend results
  @action
  setupTrendAgentResults(id, target) {
    const { $syn } = this.store;

    if (!this.trendAgents.get(id)) {
      this.trendAgents.set(
        id,
        new AgentTestResultsState({
          store: this.store,
          test: this.test,
          agent: $syn.agents.get(id)
        })
      );
    }

    if (target) {
      this.trendAgents.get(id).setupTargetResults(target);
    }
  }

  // ----- computed properties -------------------------------------

  // override to return subtest results
  @computed
  get results() {
    if (this.loadingResults) {
      return null;
    }

    return this.agentResults;
  }

  // another override to remain compliant with parent class (see above)
  @computed
  get hasResults() {
    return this.hasAgentResults;
  }

  // provides key properties for the rendering of trend charts
  @computed
  get trendTimelineBounds() {
    const { hasTrendResults, trendResults } = this;

    if (hasTrendResults) {
      // for the trend bounds, we want to ignore the adjustment ('true' arg) and just fill to the ends with what we have
      const { xMin, xMax, xAxisMin, xAxisMax, delta } = getTimelinebounds(trendResults.health_ts, true);

      return { xMin, xMax, xAxisMin, xAxisMax, delta };
    }

    return {};
  }

  // the currently selected tab id in the subtest
  @computed
  get selectedTabId() {
    return this.agents.get(this.agent_id)?.tabId;
  }

  // ----- class utils -------------------------------------

  // checks the subtest and trace results hash for fetch logic
  hasLoadedResults(agent_id, target, type = '') {
    const test_id = this.test.id;
    const hashmap = type === 'trace' ? this.loadedTraceResultsHashes : this.loadedResultsHashes;
    if (hashmap[`${test_id}`]) {
      return true;
    }
    if (agent_id && hashmap[`${test_id}.${agent_id}`]) {
      return true;
    }
    if (agent_id && target && hashmap[`${test_id}.${agent_id}.${target}`]) {
      return true;
    }
    return false;
  }

  // determines whether a trend interval is currently selected
  isTrendDetailSelected({ id }) {
    return this.selectedTrendDetailIndex === +id;
  }

  // resets the hashes for subtest and trace results
  resetLoadState() {
    this.loadedResultsHashes = {};
    this.loadedTraceResultsHashes = {};
  }

  // ----- change handlers -------------------------------------

  // processes a date selection change resulting in a fresh fetch of data
  // override from the parent to account for trend fetch logic
  onDateRangeChange({ lookbackSeconds, startDate, endDate }) {
    const { displayOptions, history } = this;
    const { $auth } = this.store;
    const dateObj = this.enforceMinLookback({ lookbackSeconds, startDate, endDate });
    const v1 = !!history?.location.pathname.includes('/v1');

    if (displayOptions.usePageComponent && !$auth.hasSudo) {
      if (v1) {
        if (!dateObj.startDate || dateObj.startDate > SYNGEST_CUTOVER) {
          history.push(`${history.location.pathname.replace('/v1', '')}`, dateObj);
        }
      } else if (dateObj.startDate && dateObj.startDate < SYNGEST_CUTOVER) {
        history.push(`${history.location.pathname}/v1`, dateObj);
      }
    }

    // don't use lookbackSeconds, does not align well across services
    if (lookbackSeconds) {
      const { start, end } = getStartAndEndFromLookback(lookbackSeconds, 120);
      dateObj.actualStartDate = start.unix();
      dateObj.actualEndDate = end.unix();
    } else {
      dateObj.actualStartDate = dateObj.startDate;
      dateObj.actualEndDate = dateObj.endDate;
    }

    // get the trend start date based on interval size
    const trendDetailIntervalLength = dateObj.actualEndDate - dateObj.actualStartDate;
    const trendStartDate = dateObj.actualStartDate - trendDetailIntervalLength * (TREND_INTERVAL_SIZE - 1);

    dateObj.trendStartDate = trendStartDate;

    this.setDateRange(dateObj);
    this.resetLoadState();
    this.fetch({
      startDate: dateObj.actualStartDate,
      endDate: dateObj.actualEndDate,
      trendStartDate: dateObj.trendStartDate
    });

    if (displayOptions.allowSettings) {
      this.store.$exports.setHash(
        {
          hashedLookbackSeconds: dateObj.lookbackSeconds,
          hashedStartDate: dateObj.startDate,
          hashedEndDate: dateObj.endDate
        },
        true,
        true
      ); // merge and replace the querystring to just be the q hash
    }
  }

  // ensures chart selections and tooltips show and follow each other when hovering in subtest results
  @action
  onChangeResultTimeMs(resultTimeMs) {
    this.resultTime = resultTimeMs / 1000;
  }
}

export default AgentResultsState;
